import React, { useState, useEffect, forwardRef } from 'react';
import { getMaintenanceByType } from '../../services/maintenanceService';

const Hero = forwardRef(({ refreshFunctionsRef }, ref) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [heroImages, setHeroImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  const fetchHeroImages = async () => {
    setLoading(true);
    try {
      const maintenanceData = await getMaintenanceByType('Hero');
      if (maintenanceData && maintenanceData.length > 0) {
        const images = maintenanceData.map(item => item.image_url).filter(Boolean);
        setHeroImages(images);
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHeroImages();
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // Register refresh function
  useEffect(() => {
    if (refreshFunctionsRef?.current) {
      const index = refreshFunctionsRef.current.length;
      refreshFunctionsRef.current.push(fetchHeroImages);
      return () => {
        refreshFunctionsRef.current = refreshFunctionsRef.current.filter((_, i) => i !== index);
      };
    }
  }, [refreshFunctionsRef]);

  useEffect(() => {
    if (heroImages.length === 0) return;
    const intervalId = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(intervalId);
  }, [heroImages.length]);

  // Full-bleed wrapper — same negative margin trick as AboutUsSection's hero image
  const fullBleedClasses =
    'relative w-screen left-1/2 -translate-x-1/2 overflow-hidden -mt-24 mb-8 sm:mb-12';

  if (loading) {
    return (
      <div className={fullBleedClasses}>
        <div className="w-full aspect-[16/10] sm:aspect-[16/9] md:aspect-[21/10] lg:aspect-[21/9] xl:aspect-[3/1] overflow-hidden min-h-[320px] sm:min-h-[450px] md:min-h-[550px] lg:min-h-[80vh] xl:min-h-[110vh] shimmer-bg bg-reptilez-green-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 border-4 border-reptilez-green-300 border-t-reptilez-green-600 rounded-full animate-spin" />
            <p className="text-gray-600 text-sm font-medium">Loading amazing races...</p>
          </div>
        </div>
      </div>
    );
  }

  if (heroImages.length === 0) {
    return (
      <div className={fullBleedClasses}>
        <div className="w-full aspect-[16/10] sm:aspect-[16/9] md:aspect-[21/10] lg:aspect-[21/9] xl:aspect-[3/1] overflow-hidden min-h-[320px] sm:min-h-[450px] md:min-h-[550px] lg:min-h-[80vh] xl:min-h-[110vh] bg-reptilez-green-50 flex items-center justify-center">
          <div className="text-center text-gray-600">
            <div className="w-20 h-20 rounded-full bg-reptilez-green-100 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <span className="text-4xl">🚴</span>
            </div>
            <p className="text-lg font-semibold">No hero images uploaded yet</p>
            <p className="text-sm mt-2">Please upload images in the admin</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${fullBleedClasses} group transition-all duration-700 ${
        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      }`}
    >
      {/* Same container as AboutUsSection: tall height so orig landscape shows fully */}
      <div className="relative w-full aspect-[16/10] sm:aspect-[16/9] md:aspect-[21/10] lg:aspect-[21/9] xl:aspect-[3/1] overflow-hidden min-h-[320px] sm:min-h-[450px] md:min-h-[550px] lg:min-h-[80vh] xl:min-h-[110vh]">
        {heroImages.map((image, index) => (
          <div
            key={`${image}-${index}`}
            className={`absolute inset-0 w-full h-full bg-no-repeat transition-opacity duration-700 ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url("${image}")`,
              // center-top keeps the subject (rider) visible on all sizes
              backgroundPosition: 'center top',
              backgroundSize: 'cover',
            }}
          />
        ))}

        {/* Support our Team button - compact pill like before, only shrinks on mobile */}
        <div
          className="absolute bottom-10 sm:bottom-12 left-1/2 -translate-x-1/2 z-20"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="relative flex items-center justify-center rounded-full h-9 px-5 sm:h-10 sm:px-6 md:h-11 md:px-8 bg-gradient-to-r from-reptilez-green-600 via-reptilez-green-500 to-reptilez-green-600 text-white text-xs sm:text-sm md:text-base font-black leading-normal tracking-wide uppercase border-2 border-reptilez-green-400/30 shadow-lg whitespace-nowrap w-fit min-w-[120px] sm:min-w-[140px]">
            <span className="relative drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
              Support our Team
            </span>
          </div>
        </div>

        {/* Progress dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          {heroImages.map((_, idx) => (
            <div
              key={idx}
              className={`rounded-full transition-all duration-500 flex-shrink-0 ${
                idx === currentIndex
                  ? 'w-8 sm:w-12 h-1.5 bg-reptilez-green-600 shadow-[0_0_10px_rgba(22,163,74,0.6)]'
                  : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/70'
              }`}
            />
          ))}
        </div>

        {/* Navigation arrows - smaller on mobile, visible on touch devices */}
        <button
          onClick={() =>
            setCurrentIndex((prev) => (prev - 1 + heroImages.length) % heroImages.length)
          }
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-white/90 backdrop-blur-sm border border-reptilez-green-200 flex items-center justify-center text-gray-700 max-sm:opacity-80 opacity-0 group-hover:opacity-100 hover:bg-reptilez-green-600 hover:border-reptilez-green-600 hover:text-white hover:scale-110 active:scale-95 transition-all duration-300 z-20 shadow-lg touch-manipulation"
          aria-label="Previous slide"
        >
          <span className="text-xl sm:text-2xl leading-none">‹</span>
        </button>
        <button
          onClick={() => setCurrentIndex((prev) => (prev + 1) % heroImages.length)}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-white/90 backdrop-blur-sm border border-reptilez-green-200 flex items-center justify-center text-gray-700 max-sm:opacity-80 opacity-0 group-hover:opacity-100 hover:bg-reptilez-green-600 hover:border-reptilez-green-600 hover:text-white hover:scale-110 active:scale-95 transition-all duration-300 z-20 shadow-lg touch-manipulation"
          aria-label="Next slide"
        >
          <span className="text-xl sm:text-2xl leading-none">›</span>
        </button>
      </div>
    </div>
  );
});

Hero.displayName = 'Hero';

export default Hero;